import * as React from "react";
import { InputText } from "@bizform/primereact/components/inputtext/InputText";
import { ColorPicker } from "@bizform/primereact/components/colorpicker/ColorPicker";
import { ColorPickerProps } from "@bizform/primereact/colorpicker";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug, DomHelper, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit, I18N } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = ColorPickerProps & {
};

type WrappedCompState = {
    value?: any;
    tabindex?: number;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    defaultColor?: string;
    showHexCode?: boolean;
    disabled?: boolean;
    tooltipAttribute?: TooltipOptions & {text?: string};
    // event
    onChange?(e: React.FormEvent<HTMLInputElement>): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi @ Chris
 * @class BfColorPicker
 * @description BfColorPicker component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfColorPicker extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_0Ft0PSQ7Dbo20k5VdM_T3mlCg1CmrA_",
        category: ComponentCategory.MISC,
        label: I18n.componentElement.colorPicker.label,
        description: I18n.componentElement.colorPicker.description,
        icon: "",
        dataType: [DataType.STRING],
        property: null
    };

    focus(): void {
        if (this.accessibility) {
            this._colorPicker.element.focus();
        } if (this._colorPicker && this._colorPicker.input) {
            this._colorPicker.input.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);
            // newState.wrappedCompState.value = newState.wrappedCompState.value ? newState.wrappedCompState.value : newState.wrappedCompState.defaultColor;
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: ""
                }
            };
            this.setState(newState, this._callbackSetState);
        } else if (!!value.match(/[0-9a-fA-F]{0,6}$/)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: value
                }
            };
            this.setState(newState);
        } else {
            if (this) this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfColorPicker)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _colorPicker: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: ""
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(prevProps) {
        if (this._colorPicker && !this.accessibility) {
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._colorPicker.input, this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
        if (this.displayMode !== ComponentElementDisplayMode.EDIT && !this.accessibility) {
            if (this._colorPicker && this._colorPicker.panel && this._colorPicker.panel.element && this._colorPicker.container) {
                this._colorPicker.panel.element.style.visibility = "hidden";
                this._colorPicker.panel.element.style.overflow = "hidden";
                if (this.displayMode === ComponentElementDisplayMode.PRINT) {
                    let inputNode = this._colorPicker.container;
                    inputNode.setAttribute("style", "");
                    for (let item in this.state.wrappedCompState["printStyle"]) {
                        inputNode.style[item] = this.state.wrappedCompState["printStyle"][item];
                    }
                    inputNode.setAttribute("class", `p-colorpicker p-component p-colorpicker-overlay ${this.state.wrappedCompState["printClassName"]}`);
                }
           }
        } else if (this._colorPicker && this._colorPicker.panel && this._colorPicker.panel.element && this._colorPicker.container && !this.accessibility) {
            this._colorPicker.panel.element.style.visibility = "visible";
            let inputNode = this._colorPicker.container;
            inputNode.setAttribute("style", "");
            inputNode.setAttribute("class", `p-colorpicker p-component p-colorpicker-overlay`);
        }
    }


    componentWillUnmount(): void {
        this._colorPicker = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(event): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: event.target.value
            }
        };
        this.setState(newState, this._callbackSetState);
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    render(): React.ReactNode {
        const {
            show
        } = this.state;

        if (!show) {
            return null;
        } else {
            const newProps = {
                ...this.state.wrappedCompState
            };
            return this._render(newProps);
        }
    }

    private _render(newProps: any): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        const styleP = newProps["printStyle"];
        const classP = newProps["printClassName"];
        delete(newProps["printStyle"]);
        delete(newProps["printClassName"]);
        if (this.accessibility) {
            const aria = {
                "aria-label": I18N.translate("{$I._app.accessibility.component.colorPicker.doInput}")
            };
            const text = newProps.value;
            newProps.value = newProps.value.length === 0 ? newProps.defaultColor : newProps.value;
            return (
                <span style={this.displayMode === ComponentElementDisplayMode.PRINT ? {display: "flex", ...styleP} : {display: "flex"}} className={this.displayMode === ComponentElementDisplayMode.PRINT ? classP : null}>
                    <InputText
                        {...aria}
                        {...newProps}
                        key={key}
                        id={this.elementId}
                        readOnly={this.displayMode === ComponentElementDisplayMode.READONLY || this.displayMode === ComponentElementDisplayMode.PRINT }
                        disabled={this.displayMode === ComponentElementDisplayMode.DISABLED}
                        style={{backgroundColor: `#${newProps.value}`, cursor: "default"}}
                        value={text}
                        ref={this._setRef}
                    />
                </span>
            );
        } else {
            return (
                <React.Fragment>
                    <ColorPicker
                        {...newProps}
                        key={key}
                        id={this.elementId}
                        inline={false}
                        readOnly={this.displayMode === ComponentElementDisplayMode.READONLY}
                        disabled={this.displayMode === ComponentElementDisplayMode.DISABLED}
                        appendTo={DomHelper.getForOverLayDom()}
                        ref={this._setRef}
                    />
                    {newProps.showHexCode ? <span className="ml-2">{newProps.value}</span> : null}
                </React.Fragment>
            );
        }
    }

    private _setRef(ref) {
        if (ref) {
            this._colorPicker = ref;
            this.readyComponent();
        }
    }
}
